/**
 * AI chat widget config — read from BUILD-time env (`import.meta.env`),
 * mirroring `features/auth/captcha/useCaptchaConfig.ts`.
 *
 * The widget is a CONTROL-PLANE consumer (CPF-NFR-033): it ships in EVERY
 * app (landing + all per-env portals) and calls ONE chat backend over
 * HTTPS, regardless of which env the customer is in. It replaces the
 * legacy Intercom bubble with ROOK's own AI assistant (RAG over
 * rook-ai-knowledge-base; READ-ONLY — it's a prompt-injection surface,
 * never a writer).
 *
 * Disabled by default — the backend isn't live yet, so wiring the widget
 * into the shells is a no-op until an operator sets, at build time:
 *   VITE_AI_CHAT_ENABLED=true
 *   VITE_AI_CHAT_API_URL=https://<chat-control-plane-host>/...
 * The URL is PUBLIC (a plain HTTPS endpoint authed by the session
 * cookie), so it's a `VITE_` build var, not a secret.
 */
export type AiChatConfig = {
  enabled: boolean;
  apiUrl: string;
  /**
   * Design-showcase mode (VITE_AI_CHAT_SHOWCASE=true): render the widget
   * with the CANNED mock sender — no backend, no entitlement gate — so the
   * decision meeting can demo the Intercom replacement. The widget also
   * activates this when the `?designShowcase` unlock is set, so customers
   * on a normal build never see it.
   */
  showcase: boolean;
};

type EnvBag = Record<string, string | undefined>;

// Cast import.meta defensively — same convention as useCaptchaConfig /
// providers.ts (avoids depending on the `vite/client` types reference
// being globally available in every consumer's tsconfig).
const importMetaEnv = (): EnvBag =>
  (import.meta as { env?: EnvBag }).env ?? {};

/**
 * `env` is injectable so unit tests pass a plain object — Vite statically
 * rewrites `import.meta.env.VITE_*` and `vi.stubEnv` doesn't reliably
 * reach a dynamic read, so injection is how the codebase tests env-gated
 * config deterministically.
 */
export const getAiChatConfig = (env: EnvBag = importMetaEnv()): AiChatConfig => {
  const enabled = env.VITE_AI_CHAT_ENABLED === "true";
  const apiUrl = (env.VITE_AI_CHAT_API_URL ?? "").trim();
  const showcase = env.VITE_AI_CHAT_SHOWCASE === "true";

  if (enabled && !apiUrl) {
    // eslint-disable-next-line no-console
    console.warn(
      "[ai-chat] VITE_AI_CHAT_ENABLED=true but VITE_AI_CHAT_API_URL is empty — widget stays hidden.",
    );
    return { enabled: false, apiUrl: "", showcase };
  }

  return { enabled, apiUrl, showcase };
};
